import { useLingui } from '@lingui/react/macro';
import { ChevronDown, ChevronRight, Crown, HandHeart, Heart, LayoutGrid, Plus, Settings } from 'lucide-react';
import { WorldcooLogoIcon } from '@worldcoo/ui/custom/worldcoo-logo-icon';
import { cn } from '@worldcoo/ui/lib/utils';
import { AppHomeDetails } from './app-home-details';

/** The finger from the danchiano phone demo, drawn half as large again for this screen's zoom. */
function HandGlyph() {
	return (
		<svg
			viewBox="0 0 20 28"
			aria-hidden
			className="absolute -top-0.5 -left-[11px] h-[42px] w-[30px] overflow-visible [filter:drop-shadow(0_0_1px_#fff)_drop-shadow(0_0_1px_#fff)_drop-shadow(0_3px_4px_rgb(0_0_0/0.35))]"
		>
			<g fill="#2a2a2a">
				<rect x="5.5" y="1" width="3.8" height="13" rx="1.9" />
				<path d="M3.6 13.2c0-1.4 1-2.4 2.4-2.4h8.2c1.6 0 2.8 1.2 2.8 2.8v4.6c0 4.4-2.8 8.3-7 8.3h-.6c-3.4 0-5.8-2.8-5.8-6.4z" />
				<rect x="2.4" y="14.9" width="2.4" height="2.8" rx="1.2" transform="rotate(-28 3.6 16.3)" />
			</g>
		</svg>
	);
}

/** The finger that taps the card open and, later, closed (`app-tap`): its point is this element's origin. */
function TapHand() {
	return (
		<span aria-hidden className="pointer-events-none absolute top-[214px] left-[196px] z-30 size-0">
			<span className="app-tap-ripple absolute -top-3 -left-3 size-6 rounded-full bg-black/15 ring-1 ring-black/25" />
			<span className="app-tap absolute block origin-top-left">
				<HandGlyph />
			</span>
		</span>
	);
}

/** "Support a project": what the collapsed card leaves in view, and the expanded one covers. */
function CampaignsPeek() {
	const { t } = useLingui();
	const campaigns = [
		{ image: '/redesign/hero-kids.jpg', name: t`Education for every child` },
		{ image: '/redesign/raised-hands.jpg', name: t`Clean water for rural villages` },
	];
	return (
		<section className="absolute top-[282px] left-5 flex flex-col gap-2.5">
			<span className="flex w-[260px] items-center justify-between">
				<span className="text-[15px] font-medium">{t`Support a project`}</span>
				<span className="grid size-6 place-items-center rounded-full bg-[#262626] text-white">
					<ChevronRight className="size-3.5" />
				</span>
			</span>
			<div className="flex gap-3">
				{campaigns.map((campaign) => (
					<div key={campaign.image} className="flex w-[164px] flex-col gap-1.5">
						<img
							src={campaign.image}
							alt=""
							width={164}
							height={116}
							loading="lazy"
							decoding="async"
							className="h-[116px] w-full rounded-2xl object-cover"
						/>
						<span className="text-[12px] leading-tight font-medium">{campaign.name}</span>
					</div>
				))}
			</div>
		</section>
	);
}

function NavigationBar() {
	return (
		<nav className="absolute inset-x-3 bottom-5 flex h-14 items-center justify-between rounded-2xl bg-[#262626] px-5 text-white">
			<LayoutGrid className="size-5 text-[#ff852b]" />
			<HandHeart className="size-5" />
			<span className="grid size-10 place-items-center rounded-full bg-[#ff8235]">
				<Plus className="size-5" strokeWidth={3} />
			</span>
			<Heart className="size-5" />
			<Settings className="size-5" />
		</nav>
	);
}

/**
 * The donors app's home (apps/donors/src/routes/home.index.tsx) on a 12 s loop. The card is always laid out open and
 * only clipped closed, so no frame relayouts: docs/notes/portals/app-home-screen.md
 */
export function AppHomeScreen(props: { zoomClassName?: string }) {
	const { t, i18n } = useLingui();
	// No currency: its placement and spacing differ between the server's ICU and the browser's (hydration).
	const amount = new Intl.NumberFormat(i18n.locale, { minimumFractionDigits: 2 }).format(223.68);
	return (
		<div
			aria-hidden
			className={cn(
				'app-screen absolute top-0 left-0 h-[637px] w-[300px] overflow-hidden bg-[#fff7ed] text-left text-wc-black',
				// The zoom that fits this 300px-wide layout to its phone: `--phone-w` x 0.946 (the screen inside the bezel) / 300.
				props.zoomClassName ?? '[zoom:0.6307] md:[zoom:0.9586]',
			)}
		>
			<header className="absolute inset-x-0 top-0 flex h-[180px] flex-col gap-3 rounded-b-3xl bg-[#262626] px-5 pt-12 text-white">
				<WorldcooLogoIcon className="h-[22px] w-auto self-start text-[#f7eee5]" />
				<span className="flex flex-col">
					<span className="text-[13px] text-white/70">{t`Hello, Laura`}</span>
					{/* One line in every locale: a second one would slide under the card. */}
					<span className="truncate text-[17px] leading-tight">{t`Your donations overview`}</span>
				</span>
			</header>

			<CampaignsPeek />
			<NavigationBar />

			<div className="app-press absolute inset-x-0 top-[132px] bottom-0 origin-[150px_66px]">
				<div className="app-panel relative h-full overflow-hidden bg-[linear-gradient(135.62deg,#ff852b_11.49%,#ff6e02_75.27%)] px-4 pt-4 text-white">
					<WorldcooLogoIcon className="pointer-events-none absolute -top-28 -left-14 h-[250px] w-[274px] text-[#f7eee5] opacity-10 mix-blend-overlay" />
					<div className="relative flex items-center justify-between">
						<span className="app-shift-start flex items-center gap-1 rounded-md bg-[#df671e] py-0.5 pr-1.5 pl-1">
							<Crown className="size-3 fill-[#ffe437] text-[#ffe437]" />
							<span className="text-[9px] font-semibold tracking-[0.4px] text-[#f5ece2] uppercase">{t`Super Donor`}</span>
						</span>
						<span className="app-shift-end flex items-center gap-0.5 rounded-md bg-white/15 py-0.5 pr-1 pl-1.5 text-[9px] font-semibold text-[#ffecc4]">
							2026
							<ChevronDown className="size-2.5" />
						</span>
					</div>
					<span className="app-shift-start relative flex flex-col">
						<span className="flex items-baseline gap-1.5">
							<span className="text-[44px] leading-[1.2] tracking-tight">{amount}</span>
							<span className="text-[26px]">€</span>
						</span>
						<span className="text-[12px] leading-4 text-[#ffdbad]">{t`You're making a real difference, keep it up!`}</span>
					</span>
					<div className="relative mt-4 flex flex-col gap-3">
						<AppHomeDetails />
					</div>
				</div>
			</div>

			<TapHand />
		</div>
	);
}
